import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SUPPRESS_NOT_FOUND_NOTIFICATION } from 'core';
import { catchError, Observable, of } from 'rxjs';
import {
  BankBranch,
  ConfirmReceiptResponse,
  Invoice,
  Receipt,
  ReceiptAdvance,
  ReceiptInvoices,
  ReceiptList,
  ReceiptListSearch,
  ReceiptStatementInfo,
  ReceiptStatementList,
  ReceiptStatementSearch,
  ReceiptsSummary,
  RegisterReceiptPayload,
  RegisterReceiptResponse,
} from '../interfaces/receipt.model';

@Injectable({ providedIn: 'root' })
export class ReceiptsService {
  constructor(private http: HttpClient) {}

  getSummary(): Observable<ReceiptsSummary> {
    return this.http.get<ReceiptsSummary>('api/incassi/riepilogo');
  }

  getReceiptsList(page: number, pageSize: number, filter: ReceiptListSearch): Observable<ReceiptList> {
    return this.http.post<ReceiptList>('api/incassi/list', filter, {
      params: { page, pagesize: pageSize },
    });
  }

  getReceipt(query: { idIncasso?: string; idBusinessPartner?: string }): Observable<Receipt | null> {
    return this.http
      .post<Receipt>('api/incassi/dettaglio', {
        idIncasso: query.idIncasso ?? null,
        idBusinessPartner: query.idBusinessPartner ?? null,
      }, {
        context: this.expectedEmptyResultContext(),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null);
          }

          throw error;
        }),
      );
  }

  getPartiteAperte(idBusinessPartner: string): Observable<ReceiptInvoices> {
    return this.http
      .post<ReceiptInvoices>(
        `api/incassi/partite/${idBusinessPartner}`,
        { status: 'open' },
        {
          params: { page: 1, pagesize: 0 },
          context: this.expectedEmptyResultContext(),
        },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of({ items: [], totalItems: 0 });
          }

          throw error;
        }),
      );
  }

  getPartiteChiuse(
    idBusinessPartner: string,
    page: number,
    pageSize: number,
    dataDocumentoDal?: Date | null,
    dataDocumentoAl?: Date | null,
  ): Observable<ReceiptInvoices> {
    return this.http.post<ReceiptInvoices>(
      `api/incassi/partite/${idBusinessPartner}`,
      {
        status: 'closed',
        dataDocumentoDal: dataDocumentoDal ? dataDocumentoDal.toISOString() : null,
        dataDocumentoAl: dataDocumentoAl ? dataDocumentoAl.toISOString() : null,
      },
      { params: { page, pagesize: pageSize } },
    );
  }

  getDettaglioEstrattoConto(idBusinessPartner: string): Observable<ReceiptStatementInfo> {
    return this.http.get<ReceiptStatementInfo>(
      `api/incassi/partite/dettaglioestrattoconto/${idBusinessPartner}`,
    );
  }

  searchStatementList(
    idBusinessPartner: string,
    page: number,
    pageSize: number,
    filter: ReceiptStatementSearch,
  ): Observable<ReceiptStatementList> {
    return this.http.post<ReceiptStatementList>(
      `api/incassi/partite/estrattoconto/${idBusinessPartner}`,
      filter,
      { params: { page, pagesize: pageSize } },
    );
  }

  registerReceipt(payload: RegisterReceiptPayload): Observable<RegisterReceiptResponse> {
    return this.http.post<RegisterReceiptResponse>('api/incassi/registra', payload);
  }

  deleteReceipt(idIncasso: string): Observable<void> {
    return this.http.delete<void>(`api/incassi/${idIncasso}`);
  }

  assignInvoices(idIncasso: string, invoices: Invoice[]): Observable<void> {
    return this.http.post<void>('api/incassi/partite/assegna', {
      idIncasso,
      assegnazioni: invoices.map(invoice => ({
        idPartita: invoice.id,
        importoAssegnato: invoice.importoAssegnato,
      })),
    });
  }

  confirmReceipt(idIncasso: string, acconti: ReceiptAdvance[]): Observable<ConfirmReceiptResponse> {
    return this.http.put<ConfirmReceiptResponse>(`api/incassi/${idIncasso}/conferma`, {
      idIncasso,
      acconti: acconti.map(acconto => ({
        codiceClienteErp: acconto.codiceClienteErp,
        importo: acconto.importo,
      })),
    });
  }

  downloadReceiptPdf(idIncasso: string): Observable<Blob> {
    return this.http.get(`api/incassi/stampe/incasso/${idIncasso}`, { responseType: 'blob' });
  }

  getFilialiBancarie(abi: string, cab: string): Observable<BankBranch[]> {
    return this.http.post<BankBranch[]>('api/incassi/lookup/banche', { abi, cab });
  }

  private expectedEmptyResultContext(): HttpContext {
    return new HttpContext().set(SUPPRESS_NOT_FOUND_NOTIFICATION, true);
  }
}
